/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as Teams from '@microsoft/teams-js';
import * as React from 'react';
import { ApprovalRequestType } from '../../../data/types.generated';
import { EntryPoint, LogCategory, PanelRegion, ScenarioName, StatusName, StepName } from '../../../logger';
import { MobileViewDetailsV2Component } from '../../../mobile/components/MobileViewDetails/MobileViewDetailsV2';
import {
    AdaptiveCardClient,
    ClientProviderType,
    FileAttachmentClient,
    FileResponse,
    GetAdaptiveCardDetailsControllerOutput,
    GetAdaptiveCardTemplateControllerOutput,
    UpdateActionClient,
    UpdateActionControllerOutput,
} from '../../../service/gen/extensibility';
import { newGuid } from '../../../utilities/AppUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ConfirmationDialogProps } from '../../common/ConfirmationDialog';
import { ViewDetailsV2Component, ViewDetailsV2ComponentProps } from './ViewDetailsV2.component';

export interface ViewDetailsV2ContainerProps {
    templateUrl?: string;
    dataPayloadUrl?: string;
    approvalType: string;
    // Callback to update the list upon user actions
    updateList?: () => void;
    // Callback on dismissing the modal of this component when specified
    onDismiss?: () => void;
}

export enum ViewDetailsV2Stage {
    // Data is being loaded
    DataLoading = 'DataLoading',
    // Data is loaded
    DataLoaded = 'DataLoaded',
    // User action is being processed
    RespondLoading = 'RespondLoading',
    // User action respond is loaded
    RespondLoaded = 'RespondLoaded',
    // Error occurred
    Error = 'Error',
}

/**
 * Render view details with adpative card
 *
 * @param props component properties
 */
export const ViewDetailsV2Container: React.FC<ViewDetailsV2ContainerProps> = (
    props: ViewDetailsV2ContainerProps
): JSX.Element => {
    const { templateUrl, dataPayloadUrl, approvalType, updateList, onDismiss } = props;

    const adaptiveCardClient = React.useRef(new AdaptiveCardClient());
    const fileAttachmentClient = React.useRef(new FileAttachmentClient());
    const updateActionClient = React.useRef(new UpdateActionClient());
    const clientRequestGUID = React.useRef(newGuid());

    const [template, setTemplate] = React.useState(null);
    const [dataPayload, setDataPayload] = React.useState(null);
    const [confirmationDialogProps, setConfirmationDialogProps] = React.useState<ConfirmationDialogProps>(null);

    // Stage regarding how the component should be rendered
    const [stage, setStage] = React.useState(ViewDetailsV2Stage.DataLoading);

    // A message with the stage, such as error message or loading message
    const [stageMessage, setStageMessage] = React.useState<string>(null);

    // Bumped to re-trigger the load effect when the user retries after an error
    const [reloadToken, setReloadToken] = React.useState(0);

    const host = getAppHost();
    const logger = host.getLogger();
    const loc = host.getLocale();
    const logCategory = LogCategory.ViewDetails;
    const ecs = host.getEcsConfig().MicrosoftTeamApprovals;
    const fileRedirectEnabled =
        (host.isIOS() && ecs.MSApprovalsFileRedirectEnabledIOS) ||
        (host.isAndroid() && ecs.MSApprovalsFileRedirectEnabledAndroid);

    // Only the AgentAndAppApprovals flow needs an explicit provider so the
    // extensibility service resolves the correct Providers:<Name> config.
    // For every other approval type the provider stays undefined (server
    // defaults to MsApprovals).
    const provider =
        approvalType === ApprovalRequestType.AgentAndAppApprovals ? ClientProviderType.AgentAndAppApprovals : undefined;

    const setStageAndMessage = React.useCallback(
        (st: ViewDetailsV2Stage, message?: string, logMessage?: string): void => {
            setStage(st);
            setStageMessage(message);

            logger.markScenario(ScenarioName.ViewDetails, ViewDetailsV2Stage[st], StatusName.NA, {
                data: {
                    ApprovalType: approvalType,
                    AdaptiveCard: true,
                },
            });

            if (st === ViewDetailsV2Stage.Error) {
                logger.logTraceException(
                    LogCategory.ViewDetails,
                    `ViewDetailsV2Stage Error: ${logMessage}, approvalType: ${approvalType}`,
                    {
                        details: approvalType,
                    }
                );
            } else {
                logger.logTraceInfo(
                    logCategory,
                    `ViewDetailsV2Stage: ${ViewDetailsV2Stage[st]}, \
                    approvalType: ${approvalType}`
                );
            }
        },
        [logger, logCategory, approvalType]
    );

    // Reset the view and re-fetch the template and details so the user can retry
    // after a load or respond failure without closing and reopening the request.
    const reload = React.useCallback((): void => {
        setTemplate(null);
        setDataPayload(null);
        setStageAndMessage(ViewDetailsV2Stage.DataLoading);
        setReloadToken((token: number): number => token + 1);
    }, [setStageAndMessage]);

    React.useEffect((): void => {
        adaptiveCardClient.current
            .getTemplate(templateUrl, provider, clientRequestGUID.current)
            .then((result: GetAdaptiveCardTemplateControllerOutput): void => setTemplate(result.CardTemplate))
            .catch((): void =>
                setStageAndMessage(
                    ViewDetailsV2Stage.Error,
                    loc.strings.error_view_details_load,
                    'Failed to get adaptive card template'
                )
            );

        adaptiveCardClient.current
            .getDetails(dataPayloadUrl, provider, clientRequestGUID.current)
            .then((result: GetAdaptiveCardDetailsControllerOutput): void => {
                setDataPayload(result.CardDetails);
            })
            .catch((): void =>
                setStageAndMessage(
                    ViewDetailsV2Stage.Error,
                    loc.strings.error_view_details_load,
                    'Failed to get adaptive card data'
                )
            );
    }, [templateUrl, dataPayloadUrl, provider, setStageAndMessage, loc, reloadToken]);

    React.useEffect((): void => {
        if (stage === ViewDetailsV2Stage.DataLoading && template && dataPayload) {
            setStageAndMessage(ViewDetailsV2Stage.DataLoaded);
        }
    }, [template, dataPayload, stage, setStageAndMessage]);

    const submitHandler = React.useCallback(
        (url: string, data: any): void => {
            setStageAndMessage(ViewDetailsV2Stage.RespondLoading);

            if (url && data) {
                const logAction = data.ActionBody?.Action ?? 'Submit';

                logger.logActionForViewDetails(
                    StepName.AdaptiveCardSubmit,
                    {
                        Response: logAction,
                        ApprovalType: approvalType,
                        AdaptiveCard: true,
                        clientRequestID: clientRequestGUID.current,
                    },
                    logAction
                );

                updateActionClient.current
                    .updateAction(clientRequestGUID.current, {
                        UpdateActionUri: url,
                        Provider: provider,
                        DataPayload: data,
                    })
                    .then((response: UpdateActionControllerOutput): void => {
                        setTemplate(response.AdaptiveCard);
                        setStageAndMessage(ViewDetailsV2Stage.RespondLoaded);
                        if (onDismiss) {
                            onDismiss();
                        }
                    })
                    .catch((): void =>
                        setStageAndMessage(
                            ViewDetailsV2Stage.Error,
                            loc.strings.error_respond_failed,
                            `${logAction} action failed in adaptive card.`
                        )
                    )
                    .finally(updateList);
            } else {
                logger.logTraceException(LogCategory.ViewDetails, 'Adaptive card submit url or data missing.', {
                    details: approvalType,
                });
            }
        },
        [setStageAndMessage, updateList, logger, approvalType, provider, loc]
    );

    const openUrlHandler = React.useCallback(
        (href: string, id: string, title: string): void => {
            if (href) {
                logger.logActionForViewDetails(
                    StepName.AdaptiveCardOpenUrl,
                    {
                        clientRequestID: clientRequestGUID.current,
                        ApprovalType: approvalType,
                        AdaptiveCard: true,
                    },
                    'OpenUrl'
                );

                if (fileRedirectEnabled) {
                    const documentHref = document
                        .getElementById('MSApprovalsRedirectionLink')
                        ?.getElementsByTagName('a')[0]?.href;
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = documentHref;
                    a.target = '_blank';
                    a.click();
                } else {
                    fileAttachmentClient.current
                        .download(href, undefined, clientRequestGUID.current)
                        .then((file: FileResponse): void => {
                            if (host.isMobile()) {
                                const reader = new FileReader();
                                reader.onloadend = (ev: ProgressEvent<FileReader>): void => {
                                    const dataUrl = ev.target.result as string;
                                    Teams.openFilePreview({
                                        entityId: newGuid(),
                                        title,
                                        type: title.split('.').pop(),
                                        objectUrl: dataUrl,
                                        downloadUrl: dataUrl,
                                        editFile: false,
                                    });
                                };

                                // Create data URL to pass file data to file preview on mobile
                                reader.readAsDataURL(file.data);
                            } else {
                                const url = URL.createObjectURL(file.data);
                                const a = document.createElement('a');
                                a.style.display = 'none';
                                a.href = url;
                                a.download = title || '';
                                a.target = '_blank';

                                const clickHandler = (): void => {
                                    setTimeout((): void => {
                                        URL.revokeObjectURL(url);
                                        a.removeEventListener('click', clickHandler);
                                    }, 150);
                                };

                                a.addEventListener('click', clickHandler, false);
                                a.click();
                            }
                        })
                        .catch((): void => {
                            const loc = host.getLocale();
                            const dismiss = (): void => setConfirmationDialogProps(null);
                            setConfirmationDialogProps({
                                isOpen: true,
                                title: loc.strings.dialog_title_download_failed,
                                subText: loc.strings.ms_approval_error_attachment,
                                dismiss,
                                actions: [
                                    {
                                        text: loc.strings.action_ok,
                                        onClick: dismiss,
                                    },
                                ],
                            });
                            logger.logTraceException(LogCategory.ViewDetails, 'Failed to download file.', {
                                details: approvalType,
                            });
                        });
                }
            } else {
                logger.logTraceException(LogCategory.ViewDetails, 'Adaptive card download url missing.', {
                    details: approvalType,
                });
            }
        },
        [approvalType, logger, host, fileRedirectEnabled]
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    logger.useCreateScenarioEffect(
        ScenarioName.ViewDetails,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        getAppHost().getPersonalAppEntryPoint(),
        EntryPoint.PersonalApp,
        PanelRegion.Modal,
        'viewApproval',
        {
            TemplateUrl: templateUrl,
            DetailsUrl: dataPayloadUrl,
            ApprovalType: approvalType,
            AdaptiveCard: true,
        }
    );

    const childProps: ViewDetailsV2ComponentProps = {
        stage,
        stageMessage,
        approvalType,
        template,
        dataPayload,
        openUrlHandler,
        submitHandler,
        confirmationDialogProps,
        correlationId: clientRequestGUID.current,
        errorAction: reload,
    };

    return host.isMobile() ? (
        <MobileViewDetailsV2Component {...childProps} />
    ) : (
        <ViewDetailsV2Component {...childProps} />
    );
};
